import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GET, POST } from '../../../services/api';
import SweetAlert2 from 'sweetalert2';
import moment from 'moment';
import * as echarts from 'echarts';

// Types para os dados do CargoCounter
interface TemporalEvolutionData {
  year: number;
  month: number;
  totalRegistros: string;
  totalItens: string;
  valorTotal: string;
  pluginspaceId?: number;
}

interface MethodSummaryData {
  cargoCounter_method: string;
  pluginspaceId?: number;
  totalRegistros: string;
  totalPluginspaces: string;
  totalItens: string;
  valorTotal: string;
  precoMedio: string;
}

interface PluginspaceSummaryData {
  pluginspaceId: number;
  totalRegistros: string;
  totalMethods: string;
  totalItens: string;
  valorTotal: string;
  primeiraData: string;
  ultimaData: string;
}

interface FilterData {
  id: number;
  method: string;
  pluginspaceId: number;
  totalItens: number;
  priceUnit: string;
  dataCarga: string;
}

interface MethodOption {
  value: string;
  label: string;
}

interface PluginspaceOption {
  id: number;
  name: string;
}

const CargoCounterPage: React.FC = () => {
  moment.locale('pt-br');

  // Função para traduzir métodos
  const translateMethod = (method: string): string => {
    const methodTranslations: Record<string, string> = {
      'GET': 'Consulta (GET)',
      'POST': 'Envio (POST)',
      'PUT': 'Atualização (PUT)',
      'DELETE': 'Exclusão (DELETE)',
      'PATCH': 'Modificação (PATCH)',
      'HEAD': 'Cabeçalho (HEAD)',
      'OPTIONS': 'Opções (OPTIONS)',
      // Adicione mais traduções conforme necessário
    };
    
    return methodTranslations[method] || method;
  };

  // Função para controlar accordion das lojas
  const toggleRowExpansion = (methodName: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(methodName)) {
        newSet.delete(methodName);
      } else {
        newSet.add(methodName);
      }
      return newSet;
    });
  };

  // Estados para os dados
  const [temporalData, setTemporalData] = useState<TemporalEvolutionData[]>([]);
  const [methodData, setMethodData] = useState<MethodSummaryData[]>([]);
  const [groupedMethodData, setGroupedMethodData] = useState<any[]>([]);
  const [pluginspaceData, setPluginspaceData] = useState<PluginspaceSummaryData[]>([]);
  const [filterData, setFilterData] = useState<FilterData[]>([]);
  const [dollarQuote, setDollarQuote] = useState<number>(5.5);
  const [dollarUpdateTime, setDollarUpdateTime] = useState<string>('');

  // Estados para filtros
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [selectedPluginspace, setSelectedPluginspace] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [lastMonths, setLastMonths] = useState<number>(1); // Otimizado: inicia com 1 mês para carregamento mais rápido

  // Estados para opções dos selects
  const [methodOptions, setMethodOptions] = useState<MethodOption[]>([]);
  const [pluginspaceOptions, setPluginspaceOptions] = useState<PluginspaceOption[]>([]);

  // Estados para loading
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [isLoadingPluginspaces, setIsLoadingPluginspaces] = useState(false);

  // Estados para TTS
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState<SpeechSynthesisUtterance | null>(null);

  // Estados para modal IA
  const [showIAModal, setShowIAModal] = useState(false);
  const [iaQuestion, setIaQuestion] = useState('');
  const [iaResponse, setIaResponse] = useState('');
  const [isLoadingIA, setIsLoadingIA] = useState(false);
  const [currentChartType, setCurrentChartType] = useState<string>('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeakingIA, setIsSpeakingIA] = useState(false);

  // Estados para accordion das lojas
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Estados para loading dos gráficos e tabelas
  const [isLoadingTemporal, setIsLoadingTemporal] = useState(false);
  const [isLoadingPluginspace, setIsLoadingPluginspace] = useState(false);
  const [isLoadingMethodTable, setIsLoadingMethodTable] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Cache local para otimização de performance
  const [cache, setCache] = useState<{
    methodOptions?: MethodOption[];
    pluginspaceOptions?: PluginspaceOption[];
    dollarQuote?: number;
    dollarUpdateTime?: string;
    lastFetch?: number;
  }>({});

  // Refs para os gráficos
  const temporalChartRef = useRef<HTMLDivElement>(null);
  const pluginspaceChartRef = useRef<HTMLDivElement>(null);

  // Refs para instâncias dos gráficos
  const temporalChartInstance = useRef<echarts.ECharts | null>(null);
  const pluginspaceChartInstance = useRef<echarts.ECharts | null>(null);

  // Função para carregar evolução temporal
  const loadTemporalEvolution = useCallback(async (overrideMonths?: number) => {
    setIsLoadingTemporal(true);
    try {
      // Usar 1 mês na inicialização, ou o valor especificado/selecionado posteriormente
      const monthsToUse = overrideMonths !== undefined ? overrideMonths : 
                         (initialLoadComplete ? lastMonths : 1);
      
      const filters = {
        method: selectedMethod || undefined,
        lastMonths: monthsToUse
      };

      const response = await POST('/account-data-cost/dashboard/temporal-evolution', { filters });
      
      if (response.success) {
        setTemporalData(response.data);
        // updateTemporalChart será chamado via useEffect
      }
    } catch (error) {
      console.error('Erro ao carregar evolução temporal:', error);
    } finally {
      setIsLoadingTemporal(false);
    }
  }, [selectedMethod, lastMonths, initialLoadComplete]);

  // Função para carregar resumo por método
  const loadMethodSummary = useCallback(async (overrideMonths?: number) => {
    setIsLoadingMethodTable(true);
    try {
      // Usar 1 mês na inicialização, ou o valor especificado/selecionado posteriormente
      const monthsToUse = overrideMonths !== undefined ? overrideMonths : 
                         (initialLoadComplete ? lastMonths : 1);
      
      const filters = {
        method: selectedMethod || undefined,
        lastMonths: monthsToUse
      };

      const response = await POST('/account-data-cost/dashboard/method-summary', { filters });
      if (response.success) {
        setMethodData(response.data);
        const grouped = groupMethodData(response.data);
        setGroupedMethodData(grouped);
      }
    } catch (error) {
      console.error('Erro ao carregar resumo por método:', error);
    } finally {
      setIsLoadingMethodTable(false);
    }
  }, [selectedMethod, lastMonths, initialLoadComplete]);

  // Função para carregar resumo por pluginspace
  const loadPluginspaceSummary = useCallback(async (overrideMonths?: number) => {
    setIsLoadingPluginspace(true);
    try {
      // Usar 1 mês na inicialização, ou o valor especificado/selecionado posteriormente
      const monthsToUse = overrideMonths !== undefined ? overrideMonths : 
                         (initialLoadComplete ? lastMonths : 1);
      
      const filters = {
        pluginspaceId: selectedPluginspace || undefined,
        lastMonths: monthsToUse
      };

      const response = await POST('/account-data-cost/dashboard/pluginspace-summary', { filters });
      if (response.success) {
        setPluginspaceData(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar resumo por pluginspace:', error);
    } finally {
      setIsLoadingPluginspace(false);
    }
  }, [selectedPluginspace, lastMonths, initialLoadComplete]);

  // Carregamento inicial otimizado com priorização
  useEffect(() => {
    if (!initialLoadComplete) {
      const loadCriticalData = async () => {
        try {
          // 1. CARREGAMENTO PRIORITÁRIO - Dados essenciais primeiro (1 mês inicial)
          await loadTemporalEvolution(1); // Força 1 mês na inicialização
          
          // 2. CARREGAMENTO SECUNDÁRIO - Em paralelo mas não-bloqueante
          setTimeout(async () => {
            try {
              await Promise.all([
                loadMethodSummary(1), // Força 1 mês na inicialização
                loadPluginspaceSummary(1) // Força 1 mês na inicialização
              ]);
            } catch (error) {
              console.error('Erro ao carregar dados secundários:', error);
            }
          }, 100);
          
          setInitialLoadComplete(true);
        } catch (error) {
          console.error('Erro ao carregar dados críticos:', error);
          SweetAlert2.fire({
            title: 'Erro',
            text: 'Erro ao carregar dados do dashboard',
            icon: 'error',
          });
        }
      };

      const loadOptionsLazy = async () => {
        // CARREGAMENTO LAZY - Opções dos filtros carregam em background
        setTimeout(async () => {
          try {
            // Carregar métodos com cache
            const cacheAge = Date.now() - (cache.lastFetch || 0);
            const cacheValid = cacheAge < 5 * 60 * 1000;
            
            if (cache.methodOptions && cacheValid) {
              setMethodOptions(cache.methodOptions);
            } else {
              setIsLoadingMethods(true);
              try {
                const methodsResponse = await GET('/account-data-cost/distinctMethods');
                if (methodsResponse.success) {
                  const options = methodsResponse.data.map((method: string) => ({
                    value: method,
                    label: method
                  }));
                  setMethodOptions(options);
                  setCache(prev => ({ ...prev, methodOptions: options, lastFetch: Date.now() }));
                }
              } finally {
                setIsLoadingMethods(false);
              }
            }

            // Carregar pluginspaces com cache
            if (cache.pluginspaceOptions && cacheValid) {
              setPluginspaceOptions(cache.pluginspaceOptions);
            } else {
              setIsLoadingPluginspaces(true);
              try {
                const pluginspacesResponse = await GET('/account-data-cost/distinctPluginspaces');
                if (pluginspacesResponse.success) {
                  setPluginspaceOptions(pluginspacesResponse.data);
                  setCache(prev => ({ ...prev, pluginspaceOptions: pluginspacesResponse.data, lastFetch: Date.now() }));
                }
              } finally {
                setIsLoadingPluginspaces(false);
              }
            }

            // Carregar cotação com cache
            const dollarCacheValid = cacheAge < 10 * 60 * 1000;
            if (cache.dollarQuote && cache.dollarUpdateTime && dollarCacheValid) {
              setDollarQuote(cache.dollarQuote);
              setDollarUpdateTime(cache.dollarUpdateTime);
            } else {
              try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                const data = await response.json();
                const quote = data.rates.BRL;
                const updateTime = moment().format('DD/MM/YYYY, HH:mm:ss');
                
                setDollarQuote(quote);
                setDollarUpdateTime(updateTime);
                setCache(prev => ({ ...prev, dollarQuote: quote, dollarUpdateTime: updateTime, lastFetch: Date.now() }));
              } catch (error) {
                console.error('Erro ao obter cotação do dólar:', error);
                setDollarQuote(cache.dollarQuote || 5.5);
                setDollarUpdateTime(cache.dollarUpdateTime || '');
              }
            }
          } catch (error) {
            console.error('Erro ao carregar opções:', error);
          }
        }, 200);
      };

      loadCriticalData();
      loadOptionsLazy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem

  // Monitorar mudanças no filtro de meses apenas após carregamento inicial
  useEffect(() => {
    if (initialLoadComplete) {
      // Só recarrega quando usuário muda o filtro após carregamento inicial
      loadTemporalEvolution(lastMonths);
    }
  }, [lastMonths, initialLoadComplete, loadTemporalEvolution]);

  // Monitorar mudanças no filtro de meses para o resumo por método
  useEffect(() => {
    if (initialLoadComplete) {
      loadMethodSummary(lastMonths);
    }
  }, [lastMonths, initialLoadComplete, loadMethodSummary]);

  // Monitorar mudanças no filtro de meses para o resumo por pluginspace
  useEffect(() => {
    if (initialLoadComplete) {
      loadPluginspaceSummary(lastMonths);
    }
  }, [lastMonths, initialLoadComplete, loadPluginspaceSummary]);

  // Função para agrupar dados por método
  const groupMethodData = (data: MethodSummaryData[]) => {
    const groupedData = data.reduce((acc, item) => {
      const method = item.cargoCounter_method;
      
      if (!acc[method]) {
        acc[method] = {
          method: method,
          totalRegistros: 0,
          totalPluginspaces: new Set<number>(),
          totalItens: 0,
          valorTotal: 0
        };
      }
      
      acc[method].totalRegistros += parseInt(item.totalRegistros);
      if (item.pluginspaceId) {
        acc[method].totalPluginspaces.add(item.pluginspaceId);
      }
      acc[method].totalItens += parseInt(item.totalItens);
      acc[method].valorTotal += parseFloat(item.valorTotal);
      
      return acc;
    }, {} as Record<string, {
      method: string;
      totalRegistros: number;
      totalPluginspaces: Set<number>;
      totalItens: number;
      valorTotal: number;
    }>);

    // Converter para array e ordenar por valor total (decrescente)
    const groupedArray = Object.values(groupedData).map(item => ({
      ...item,
      pluginspacesCount: item.totalPluginspaces.size,
      pluginspacesList: Array.from(item.totalPluginspaces).sort((a, b) => a - b)
    })).sort((a, b) => b.valorTotal - a.valorTotal);

    setGroupedMethodData(groupedArray);
    return groupedArray;
  };

  // Função para aplicar filtros
  const applyFilters = async () => {
    setIsApplyingFilters(true);
    try {
      const filters: any = {};
      
      if (selectedMethod) filters.method = selectedMethod;
      if (selectedPluginspace) filters.pluginspaceId = selectedPluginspace;
      if (startDate) filters.dataInicial = startDate;
      if (endDate) filters.dataFinal = endDate;

      const response = await POST('/account-data-cost/filter', filters);
      
      if (response.success) {
        setFilterData(response.data);
        // Recarregar dados principais baseado nos filtros selecionados pelo usuário
        await Promise.all([
          loadTemporalEvolution(lastMonths), // Usar o período selecionado pelo usuário
          loadMethodSummary(lastMonths), // Aplicar filtro de meses também
          loadPluginspaceSummary(lastMonths) // Aplicar filtro de meses também
        ]);
      }
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
      SweetAlert2.fire({
        title: 'Erro',
        text: 'Erro ao aplicar filtros',
        icon: 'error',
      });
    } finally {
      setIsApplyingFilters(false);
    }
  };

  // Função para limpar filtros
  const clearFilters = () => {
    setSelectedMethod('');
    setSelectedPluginspace('');
    setStartDate('');
    setEndDate('');
    setLastMonths(1); // Reset para 1 mês ao invés de 6
    setFilterData([]);
    
    // Recarregar dados após limpar filtros (volta para 1 mês)
    Promise.all([
      loadTemporalEvolution(1), // Força 1 mês após limpar filtros
      loadMethodSummary(1), // Força 1 mês após limpar filtros
      loadPluginspaceSummary(1) // Força 1 mês após limpar filtros
    ]);
  };

  // Funções para atualizar gráficos
  const updateTemporalChart = useCallback((data: TemporalEvolutionData[]) => {
    if (!temporalChartRef.current) return;

    if (temporalChartInstance.current) {
      temporalChartInstance.current.dispose();
    }

    temporalChartInstance.current = echarts.init(temporalChartRef.current);

    const months = data.map(item => `${item.month}/${item.year}`);
    const registros = data.map(item => parseInt(item.totalRegistros));
    const valores = data.map(item => parseFloat(item.valorTotal) * dollarQuote);

    const option = {
      title: {
        text: 'Evolução Temporal',
        left: 'center',
        top: 20
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((param: any) => {
            if (param.seriesName === 'Registros') {
              result += `${param.marker} ${param.seriesName}: ${param.value.toLocaleString()}<br/>`;
            } else {
              result += `${param.marker} ${param.seriesName}: R$ ${param.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br/>`;
            }
          });
          return result;
        }
      },
      legend: {
        data: ['Registros', 'Valor Total (R$)'],
        top: 50
      },
      xAxis: {
        type: 'category',
        data: months
      },
      yAxis: [
        {
          type: 'value',
          name: 'Registros',
          position: 'left'
        },
        {
          type: 'value',
          name: 'Valor (R$)',
          position: 'right'
        }
      ],
      series: [
        {
          name: 'Registros',
          type: 'bar',
          data: registros,
          itemStyle: { color: '#5470c6' }
        },
        {
          name: 'Valor Total (R$)',
          type: 'line',
          yAxisIndex: 1,
          data: valores,
          itemStyle: { color: '#91cc75' }
        }
      ]
    };

    temporalChartInstance.current.setOption(option);
  }, [dollarQuote]);

  const updatePluginspaceChart = useCallback((data: PluginspaceSummaryData[]) => {
    if (!pluginspaceChartRef.current) return;

    if (pluginspaceChartInstance.current) {
      pluginspaceChartInstance.current.dispose();
    }

    pluginspaceChartInstance.current = echarts.init(pluginspaceChartRef.current);

    const pluginspaces = data.map(item => `Loja ${item.pluginspaceId}`);
    const valores = data.map(item => parseFloat(item.valorTotal) * dollarQuote);
    const registros = data.map(item => parseInt(item.totalRegistros));

    const option = {
      title: {
        text: 'Resumo por Pluginspace',
        left: 'center',
        top: 20
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((param: any) => {
            if (param.seriesName === 'Registros') {
              result += `${param.marker} ${param.seriesName}: ${param.value.toLocaleString()}<br/>`;
            } else {
              result += `${param.marker} ${param.seriesName}: R$ ${param.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br/>`;
            }
          });
          return result;
        }
      },
      legend: {
        data: ['Registros', 'Valor Total (R$)'],
        top: 50
      },
      xAxis: {
        type: 'category',
        data: pluginspaces,
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Registros',
          position: 'left'
        },
        {
          type: 'value',
          name: 'Valor (R$)',
          position: 'right'
        }
      ],
      series: [
        {
          name: 'Registros',
          type: 'bar',
          data: registros,
          itemStyle: { color: '#fac858' }
        },
        {
          name: 'Valor Total (R$)',
          type: 'line',
          yAxisIndex: 1,
          data: valores,
          itemStyle: { color: '#ee6666' }
        }
      ]
    };

    pluginspaceChartInstance.current.setOption(option);
  }, [dollarQuote]);

  // Atualizar gráficos quando os dados mudarem
  useEffect(() => {
    if (temporalData.length > 0) {
      updateTemporalChart(temporalData);
    }
  }, [temporalData, dollarQuote, updateTemporalChart]);

  useEffect(() => {
    if (methodData.length > 0) {
      groupMethodData(methodData);
    }
  }, [methodData, dollarQuote]);

  useEffect(() => {
    if (pluginspaceData.length > 0) {
      updatePluginspaceChart(pluginspaceData);
    }
  }, [pluginspaceData, dollarQuote, updatePluginspaceChart]);

  // Funções TTS
  const speakChartAnalysis = (chartType: string) => {
    if (isSpeaking) {
      stopSpeaking();
      return;
    }

    let analysisText = '';

    switch (chartType) {
      case 'temporal':
        analysisText = generateTemporalAnalysis();
        break;
      case 'method':
        analysisText = generateMethodAnalysis();
        break;
      case 'pluginspace':
        analysisText = generatePluginspaceAnalysis();
        break;
      default:
        analysisText = 'Análise não disponível para este gráfico.';
    }

    const utterance = new SpeechSynthesisUtterance(analysisText);
    utterance.lang = 'pt-BR';
    utterance.rate = 0.8;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentUtterance(null);
    };

    utterance.onerror = (event) => {
      if (event.error !== 'canceled' && event.error !== 'interrupted') {
        console.error('TTS: Speech error event:', event.error);
        SweetAlert2.fire({
          title: 'Erro no TTS',
          text: 'Erro na síntese de voz. Tente novamente.',
          icon: 'error',
        });
      }
      setIsSpeaking(false);
      setCurrentUtterance(null);
    };

    setCurrentUtterance(utterance);
    speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (currentUtterance) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      setCurrentUtterance(null);
    }
  };

  // Funções para gerar análises
  const generateTemporalAnalysis = (): string => {
    if (temporalData.length === 0) return 'Nenhum dado temporal disponível para análise.';

    const totalRegistros = temporalData.reduce((sum, item) => sum + parseInt(item.totalRegistros), 0);
    const totalValor = temporalData.reduce((sum, item) => sum + parseFloat(item.valorTotal), 0) * dollarQuote;

    return `Análise da evolução temporal: Foram processados ${totalRegistros.toLocaleString()} registros, 
      com um valor total de ${totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. 
      Os dados mostram a distribuição de operações ao longo dos últimos ${lastMonths} meses.`;
  };

  const generateMethodAnalysis = (): string => {
    if (groupedMethodData.length === 0) return 'Nenhum dado de método disponível para análise.';

    const totalMethods = groupedMethodData.length;
    const totalValor = groupedMethodData.reduce((sum, item) => sum + item.valorTotal, 0) * dollarQuote;
    const topMethod = groupedMethodData[0]; // Já ordenado por valor decrescente

    const totalPluginspaces = groupedMethodData.reduce((acc, item) => {
      item.totalPluginspaces.forEach((ps: number) => acc.add(ps));
      return acc;
    }, new Set<number>()).size;

    const totalRegistros = groupedMethodData.reduce((sum, item) => sum + item.totalRegistros, 0);
    const totalItens = groupedMethodData.reduce((sum, item) => sum + item.totalItens, 0);

    return `Análise da tabela de métodos: Foram identificados ${totalMethods} métodos únicos distribuídos em ${totalPluginspaces} pluginspaces, 
      totalizando ${totalRegistros.toLocaleString('pt-BR')} registros e ${totalItens.toLocaleString('pt-BR')} itens processados, 
      com valor total de ${totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. 
      O método com maior representatividade é ${topMethod.method}, 
      responsável por ${(topMethod.valorTotal * dollarQuote).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} 
      distribuído em ${topMethod.pluginspacesCount} pluginspaces, 
      representando ${((topMethod.valorTotal / groupedMethodData.reduce((sum, item) => sum + item.valorTotal, 0)) * 100).toFixed(1)}% do total.`;
  };

  const generatePluginspaceAnalysis = (): string => {
    if (pluginspaceData.length === 0) return 'Nenhum dado de pluginspace disponível para análise.';

    const totalPluginspaces = pluginspaceData.length;
    const totalValor = pluginspaceData.reduce((sum, item) => sum + parseFloat(item.valorTotal), 0) * dollarQuote;
    const topPluginspace = pluginspaceData.reduce((prev, current) => 
      parseFloat(prev.valorTotal) > parseFloat(current.valorTotal) ? prev : current
    );

    return `Análise por pluginspace: Foram analisadas ${totalPluginspaces} lojas, 
      com valor total de ${totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. 
      A loja com maior valor é o pluginspace ${topPluginspace.pluginspaceId}, 
      com ${(parseFloat(topPluginspace.valorTotal) * dollarQuote).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`;
  };

  // Funções do modal IA
  const openIAModal = (chartType: string) => {
    setCurrentChartType(chartType);
    setIaQuestion('');
    setIaResponse('');
    setShowIAModal(true);
  };

  const closeIAModal = () => {
    setShowIAModal(false);
    setIaQuestion('');
    setIaResponse('');
    setCurrentChartType('');
    if (isSpeakingIA) {
      speechSynthesis.cancel();
      setIsSpeakingIA(false);
    }
  };

  const submitIAAnalysis = async () => {
    if (!iaQuestion.trim()) {
      SweetAlert2.fire({
        title: 'Atenção',
        text: 'Por favor, digite uma pergunta para análise.',
        icon: 'warning',
      });
      return;
    }

    setIsLoadingIA(true);
    try {
      let chartData = {};
      
      switch (currentChartType) {
        case 'temporal':
          chartData = temporalData;
          break;
        case 'method':
          chartData = methodData;
          break;
        case 'pluginspace':
          chartData = pluginspaceData;
          break;
      }

      const response = await POST('/analyse-ia/analyse-grafic', {
        metadata: {
          chartType: currentChartType,
          question: iaQuestion,
          data: chartData,
          dollarQuote: dollarQuote
        }
      });

      if (response.success) {
        setIaResponse(response.data.analysis || 'Análise realizada com sucesso.');
      } else {
        throw new Error('Erro na análise IA');
      }
    } catch (error) {
      console.error('Erro na análise IA:', error);
      setIaResponse('Desculpe, ocorreu um erro na análise. Tente novamente.');
    } finally {
      setIsLoadingIA(false);
    }
  };

  const toggleSpeechToText = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      SweetAlert2.fire({
        title: 'Recurso não disponível',
        text: 'Seu navegador não suporta reconhecimento de voz.',
        icon: 'warning',
      });
      return;
    }

    if (isListening) {
      setIsListening(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'pt-BR';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setIaQuestion(transcript);
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
      SweetAlert2.fire({
        title: 'Erro',
        text: 'Erro no reconhecimento de voz. Tente novamente.',
        icon: 'error',
      });
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const toggleIAResultSpeech = () => {
    if (isSpeakingIA) {
      speechSynthesis.cancel();
      setIsSpeakingIA(false);
      return;
    }

    if (!iaResponse) {
      SweetAlert2.fire({
        title: 'Atenção',
        text: 'Não há resposta para reproduzir.',
        icon: 'warning',
      });
      return;
    }

    const utterance = new SpeechSynthesisUtterance(iaResponse);
    utterance.lang = 'pt-BR';
    utterance.rate = 0.8;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeakingIA(true);
    };

    utterance.onend = () => {
      setIsSpeakingIA(false);
    };

    utterance.onerror = (event) => {
      if (event.error !== 'canceled' && event.error !== 'interrupted') {
        console.log('TTS: Speech error event:', event.error);
        SweetAlert2.fire({
          title: 'Erro no TTS',
          text: 'Erro na síntese de voz. Tente novamente.',
          icon: 'error',
        });
      }
      setIsSpeakingIA(false);
    };

    speechSynthesis.speak(utterance);
  };

  const printIAResult = () => {
    if (!iaResponse) {
      SweetAlert2.fire({
        title: 'Atenção',
        text: 'Não há resultado para imprimir.',
        icon: 'warning',
      });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Análise IA - CargoCounter</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; }
              .content { line-height: 1.6; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Análise IA - CargoCounter</h1>
              <p>Gráfico: ${currentChartType.charAt(0).toUpperCase() + currentChartType.slice(1)}</p>
              <p>Data: ${moment().format('DD/MM/YYYY HH:mm')}</p>
            </div>
            <div class="content">
              <h3>Pergunta:</h3>
              <p>${iaQuestion}</p>
              <h3>Resposta:</h3>
              <p>${iaResponse}</p>
            </div>
            <div class="footer">
              <p>Gerado em ${moment().format('DD/MM/YYYY HH:mm:ss')}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Calcular totais para os cards
  const calculateTotals = () => {
    const totalRegistros = filterData.length > 0 
      ? filterData.length 
      : temporalData.reduce((sum, item) => sum + parseInt(item.totalRegistros), 0);

    const totalItens = filterData.length > 0
      ? filterData.reduce((sum, item) => sum + item.totalItens, 0)
      : temporalData.reduce((sum, item) => sum + parseInt(item.totalItens), 0);

    const lojasUnicas = filterData.length > 0
      ? new Set(filterData.map(item => item.pluginspaceId)).size
      : pluginspaceData.length;

    const custoTotal = filterData.length > 0
      ? filterData.reduce((sum, item) => sum + parseFloat(item.priceUnit) * item.totalItens, 0) * dollarQuote
      : temporalData.reduce((sum, item) => sum + parseFloat(item.valorTotal), 0) * dollarQuote;

    return {
      totalRegistros,
      totalItens,
      lojasUnicas,
      custoTotal
    };
  };

  const totals = calculateTotals();

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h1 className="card-title mb-0">
                <i className="bi bi-graph-up-arrow me-2"></i>
                Dashboard CargoCounter
              </h1>
              <p className="card-text text-muted">
                Análise completa de operações e custos do sistema CargoCounter
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white h-100">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="card-title mb-1">Total de Registros</h6>
                <h3 className="mb-0">{totals.totalRegistros.toLocaleString()}</h3>
              </div>
              <div className="ms-3">
                <i className="bi bi-file-earmark-text fs-1"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-success text-white h-100">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="card-title mb-1">Consumo Total</h6>
                <h3 className="mb-0">{totals.totalItens.toLocaleString()}</h3>
              </div>
              <div className="ms-3">
                <i className="bi bi-speedometer2 fs-1"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-warning text-white h-100">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="card-title mb-1">Lojas Únicas</h6>
                <h3 className="mb-0">{totals.lojasUnicas.toLocaleString()}</h3>
              </div>
              <div className="ms-3">
                <i className="bi bi-shop fs-1"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-danger text-white h-100">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="card-title mb-1">Custo Total</h6>
                <h3 className="mb-0">{totals.custoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</h3>
                <small className="d-block">Cotação USD: {dollarQuote.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</small>
                {dollarUpdateTime && (
                  <small className="d-block opacity-75">
                    <i className="bi bi-clock me-1"></i>
                    Última atualização: {dollarUpdateTime}
                    <br />
                    <i className="bi bi-cloud-arrow-down me-1"></i>
                    Cotação obtida via API externa
                  </small>
                )}
              </div>
              <div className="ms-3">
                <i className="bi bi-currency-dollar fs-1"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-funnel me-2"></i>
                Filtros
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Data Inicial</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Data Final</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Método</label>
                  <select
                    className="form-select"
                    value={selectedMethod}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    disabled={isLoadingMethods}
                  >
                    <option value="">Todos os métodos</option>
                    {methodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {isLoadingMethods && <small className="text-muted">Carregando métodos...</small>}
                </div>

                <div className="col-md-3">
                  <label className="form-label">Loja (Pluginspace)</label>
                  <select
                    className="form-select"
                    value={selectedPluginspace}
                    onChange={(e) => setSelectedPluginspace(e.target.value ? parseInt(e.target.value) : '')}
                    disabled={isLoadingPluginspaces}
                  >
                    <option value="">Todas as lojas</option>
                    {pluginspaceOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.id} - {option.name}
                      </option>
                    ))}
                  </select>
                  {isLoadingPluginspaces && <small className="text-muted">Carregando lojas...</small>}
                </div>

                <div className="col-md-3">
                  <label className="form-label">Últimos Meses (Temporal)</label>
                  <select
                    className="form-select"
                    value={lastMonths}
                    onChange={(e) => setLastMonths(parseInt(e.target.value))}
                  >
                    <option value={1}>1 mês</option>
                    <option value={3}>3 meses</option>
                    <option value={6}>6 meses</option>
                    <option value={12}>12 meses</option>
                    <option value={24}>24 meses</option>
                    <option value={36}>36 meses</option>
                    <option value={48}>48 meses</option>
                    <option value={60}>60 meses</option>
                    <option value={72}>72 meses</option>
                    <option value={84}>84 meses</option>
                    <option value={96}>96 meses</option>
                    <option value={108}>108 meses</option>
                    <option value={120}>120 meses</option>
                  </select>
                </div>

                <div className="col-md-9">
                  <label className="form-label">&nbsp;</label>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={applyFilters}
                      disabled={isApplyingFilters}
                    >
                      {isApplyingFilters ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Aplicando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-2"></i>
                          Aplicar Filtros
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={clearFilters}
                      disabled={isApplyingFilters}
                    >
                      <i className="bi bi-eraser me-2"></i>
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Evolução Temporal */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0" style={{ marginTop: '10px', textAlign: 'center', flex: 1 }}>
                <i className="bi bi-graph-up me-2"></i>
                Evolução Temporal - Último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'}
              </h5>
              <div className="btn-group gap-2" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => speakChartAnalysis('temporal')}
                  title="Ouvir análise do gráfico"
                >
                  <i className="bi bi-volume-up fs-5"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => openIAModal('temporal')}
                  title="Análise IA personalizada"
                >
                  <i className="bi bi-robot fs-5"></i>
                </button>
              </div>
            </div>
            <div className="card-body">
              {isLoadingTemporal ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                  <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted mb-2">
                      <strong>Processando Evolução Temporal</strong>
                    </p>
                    <small className="text-muted">
                      Carregando dados do{lastMonths === 1 ? '' : 's'} último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'} e gerando gráfico...
                    </small>
                  </div>
                </div>
              ) : (
                <div ref={temporalChartRef} style={{ width: '100%', height: '400px' }}></div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Resumo por Pluginspace */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0" style={{ marginTop: '10px', textAlign: 'center', flex: 1 }}>
                <i className="bi bi-bar-chart me-2"></i>
                Resumo por Pluginspace (Lojas) - Último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'}
              </h5>
              <div className="btn-group gap-2" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => speakChartAnalysis('pluginspace')}
                  title="Ouvir análise do gráfico"
                >
                  <i className="bi bi-volume-up fs-5"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => openIAModal('pluginspace')}
                  title="Análise IA personalizada"
                >
                  <i className="bi bi-robot fs-5"></i>
                </button>
              </div>
            </div>
            <div className="card-body">
              {isLoadingPluginspace ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                  <div className="text-center">
                    <div className="spinner-border text-info mb-3" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted mb-2">
                      <strong>Processando Resumo por Pluginspace (Lojas)</strong>
                    </p>
                    <small className="text-muted">
                      Coletando dados do{lastMonths === 1 ? '' : 's'} último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'} de todas as lojas...
                    </small>
                  </div>
                </div>
              ) : (
                <div ref={pluginspaceChartRef} style={{ width: '100%', height: '400px' }}></div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Resumo por Método */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0" style={{ marginTop: '10px', textAlign: 'center', flex: 1 }}>
                <i className="bi bi-table me-2"></i>
                Resumo por Método de Operação - Último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'}
              </h5>
              <div className="btn-group gap-2" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => speakChartAnalysis('method')}
                  title="Ouvir análise da tabela"
                >
                  <i className="bi bi-volume-up fs-5"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => openIAModal('method')}
                  title="Análise IA personalizada"
                >
                  <i className="bi bi-robot fs-5"></i>
                </button>
              </div>
            </div>
            <div className="card-body">
              {isLoadingMethodTable ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                  <div className="text-center">
                    <div className="spinner-border text-success mb-3" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="text-muted mb-2">
                      <strong>Processando Resumo por Método</strong>
                    </p>
                    <small className="text-muted">
                      Analisando dados do{lastMonths === 1 ? '' : 's'} último{lastMonths === 1 ? '' : 's'} {lastMonths} mês{lastMonths === 1 ? '' : 'es'} e calculando estatísticas...
                    </small>
                  </div>
                </div>
              ) : groupedMethodData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">
                          <i className="bi bi-gear me-1"></i>
                          Método de Operação
                        </th>
                        <th scope="col" className="text-center">
                          <i className="bi bi-file-earmark-text me-1"></i>
                          Registros
                        </th>
                        <th scope="col" className="text-center">
                          <i className="bi bi-speedometer2 me-1"></i>
                          Itens
                        </th>
                        <th scope="col" className="text-center">
                          <i className="bi bi-shop me-1"></i>
                          Lojas
                        </th>
                        <th scope="col" className="text-end">
                          <i className="bi bi-currency-dollar me-1"></i>
                          Valor Total
                        </th>
                        <th scope="col" className="text-center">
                          <i className="bi bi-percent me-1"></i>
                          % do Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedMethodData.map((item, index) => {
                        const totalGeral = groupedMethodData.reduce((sum, row) => sum + row.valorTotal, 0);
                        const percentual = (item.valorTotal / totalGeral) * 100;
                        const valorEmReais = item.valorTotal * dollarQuote;
                        
                        return (
                          <tr key={item.method}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className={`badge bg-${index % 2 === 0 ? 'primary' : 'secondary'} me-2`}>
                                  {index + 1}
                                </div>
                                <strong>{translateMethod(item.method)}</strong>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-info">
                                {item.totalRegistros.toLocaleString('pt-BR')}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-warning text-dark">
                                {item.totalItens.toLocaleString('pt-BR')}
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="d-flex flex-column align-items-center">
                                <button
                                  className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                                  onClick={() => toggleRowExpansion(item.method)}
                                  title="Clique para ver/ocultar IDs das lojas"
                                >
                                  <span className="badge bg-success">
                                    {item.pluginspacesCount}
                                  </span>
                                  <span>loja{item.pluginspacesCount !== 1 ? 's' : ''}</span>
                                  <i className={`bi ${expandedRows.has(item.method) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                </button>
                                
                                {expandedRows.has(item.method) && item.pluginspacesList && item.pluginspacesList.length > 0 && (
                                  <div className="mt-2 p-2 bg-light rounded border" style={{ maxHeight: '120px', overflowY: 'auto', width: '100%' }}>
                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                      {item.pluginspacesList.map((psId: number) => (
                                        <span key={psId} className="badge bg-secondary small">
                                          {psId}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="text-end">
                              <strong className="text-success">
                                {valorEmReais.toLocaleString('pt-BR', { 
                                  style: 'currency', 
                                  currency: 'BRL' 
                                })}
                              </strong>
                            </td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <div 
                                  className="progress me-2" 
                                  style={{ width: '60px', height: '8px' }}
                                >
                                  <div 
                                    className="progress-bar bg-primary" 
                                    role="progressbar" 
                                    style={{ width: `${percentual}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted">
                                  {percentual.toFixed(1)}%
                                </small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <th scope="row">
                          <strong>TOTAIS</strong>
                        </th>
                        <th className="text-center">
                          <strong className="text-primary">
                            {groupedMethodData.reduce((sum, item) => sum + item.totalRegistros, 0).toLocaleString('pt-BR')}
                          </strong>
                        </th>
                        <th className="text-center">
                          <strong className="text-warning">
                            {groupedMethodData.reduce((sum, item) => sum + item.totalItens, 0).toLocaleString('pt-BR')}
                          </strong>
                        </th>
                        <th className="text-center">
                          <strong className="text-success">
                            {groupedMethodData.reduce((acc, item) => {
                              item.totalPluginspaces.forEach((ps: number) => acc.add(ps));
                              return acc;
                            }, new Set<number>()).size}
                          </strong>
                        </th>
                        <th className="text-end">
                          <strong className="text-success">
                            {(groupedMethodData.reduce((sum, item) => sum + item.valorTotal, 0) * dollarQuote).toLocaleString('pt-BR', { 
                              style: 'currency', 
                              currency: 'BRL' 
                            })}
                          </strong>
                        </th>
                        <th className="text-center">
                          <strong className="text-muted">100%</strong>
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-table display-4 text-muted"></i>
                  <p className="text-muted mt-2">Nenhum dado disponível para exibir</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal IA Personalizada */}
      {showIAModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-robot me-2"></i>
                  Análise IA Personalizada - {currentChartType.charAt(0).toUpperCase() + currentChartType.slice(1)}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeIAModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Seção de dados que serão enviados para IA */}
                <div className="mb-4">
                  <div className="card bg-light">
                    <div className="card-header">
                      <h6 className="card-title mb-0">
                        <i className="bi bi-database me-2"></i>
                        Dados que serão enviados para análise IA
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Tipo de Gráfico:</strong> {currentChartType.charAt(0).toUpperCase() + currentChartType.slice(1)}
                        </div>
                        <div className="col-md-6">
                          <strong>Cotação do Dólar:</strong> {dollarQuote.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </div>
                      </div>
                      <hr />
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        <pre className="small bg-white p-2 border rounded">
                          {JSON.stringify(
                            currentChartType === 'temporal' ? temporalData :
                            currentChartType === 'method' ? methodData :
                            pluginspaceData, 
                            null, 2
                          )}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Sua pergunta sobre o gráfico:</label>
                  <div className="input-group">
                    <textarea
                      className="form-control"
                      rows={3}
                      value={iaQuestion}
                      onChange={(e) => setIaQuestion(e.target.value)}
                      placeholder="Digite sua pergunta sobre os dados do gráfico..."
                      disabled={isLoadingIA}
                    />
                    <button
                      className={`btn ${isListening ? 'btn-danger' : 'btn-outline-secondary'}`}
                      type="button"
                      onClick={toggleSpeechToText}
                      title={isListening ? 'Parar gravação' : 'Iniciar gravação de voz'}
                      disabled={isLoadingIA}
                    >
                      <i className={`bi ${isListening ? 'bi-mic-fill' : 'bi-mic'}`}></i>
                    </button>
                  </div>
                  {isListening && <small className="text-info">🎤 Ouvindo... Fale agora!</small>}
                </div>

                {iaResponse && (
                  <div className="mb-3">
                    <label className="form-label">Resposta da IA:</label>
                    <div className="p-3 bg-light rounded">
                      <p className="mb-0">{iaResponse}</p>
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        className={`btn btn-sm ${isSpeakingIA ? 'btn-danger' : 'btn-outline-primary'} me-2`}
                        onClick={toggleIAResultSpeech}
                      >
                        <i className={`bi ${isSpeakingIA ? 'bi-stop-fill' : 'bi-volume-up'} me-1`}></i>
                        {isSpeakingIA ? 'Parar' : 'Ouvir'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={printIAResult}
                      >
                        <i className="bi bi-printer me-1"></i>
                        Imprimir
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeIAModal}
                  disabled={isLoadingIA}
                >
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitIAAnalysis}
                  disabled={isLoadingIA || !iaQuestion.trim()}
                >
                  {isLoadingIA ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Analisando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Analisar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargoCounterPage;